/**
 * Quiet auth-persistence telemetry for native APK debugging.
 *
 * Two snapshots are recorded in memory (and optionally logged) so future
 * debugging can check whether the force-close persistence fix is working
 * on a freshly-built APK. No UI is rendered by this module.
 *
 *   coldBoot  — populated once by nativeInit.ts during app startup.
 *               Answers: "Did the Supabase session survive the last
 *               force-close in @capacitor/preferences?"
 *
 *   lastWrite — populated by the Supabase storage adapter every time
 *               supabase-js writes the auth token. Answers: "When I
 *               signed in, did the session actually get written into
 *               @capacitor/preferences durably?"
 */

export type ColdBootTelemetry = {
  preferences_supabase_session_present: boolean;
  preferences_remember_me_present: boolean;
  localstorage_supabase_session_present: boolean;
  localstorage_remember_me_present: boolean;
  captured_at: number;
};

export type WriteTelemetry = {
  key: string;
  status: "success" | "fail" | "retry-success";
  error?: string;
  at: number;
};

type State = {
  coldBoot: ColdBootTelemetry | null;
  lastWrite: WriteTelemetry | null;
};

const state: State = { coldBoot: null, lastWrite: null };
const listeners = new Set<() => void>();

const notify = () => listeners.forEach((l) => l());

export const setColdBootTelemetry = (v: ColdBootTelemetry) => {
  state.coldBoot = v;
  notify();
};

export const setWriteTelemetry = (v: WriteTelemetry) => {
  state.lastWrite = v;
  notify();
};

export const getAuthTelemetry = (): State => ({ ...state });

export const subscribeAuthTelemetry = (fn: () => void): (() => void) => {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
};
